import AppContext from 'App/AppContext';
import { IPDefaultButton, IPFontIcon, IPImage, IPPrimaryButton, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPProgressIndicator } from 'baseComponents/ProgressIndicator/IPProgressIndicator';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import { DialogOk } from 'components/Dialogs/DialogOk';
import InternationalPhoneField, {
  isValidNumber,
  translateLanguageCodeToCountryCode,
} from 'components/Utils/PhoneNumber/PhoneNumber';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { CopilotOnboardingProject } from 'models/copilot/copilotOnboardingProject';
import Language from 'models/language';
import { ISONorm, Standards } from 'models/norm';
import { AIEnabled, DefLanguageCode } from 'models/setting';
import { LeadSources_Instant27001, TenantOnboarding } from 'models/tenant';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotFinishOnboarding, apiCopilotFinishOnboardingProgress } from 'services/Api/copilotService';
import { apiGetTenantOnboardingState } from 'services/Api/tenantService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import { IPSharedColors } from 'themes/runtime/fluentColors';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';

interface ITrialWelcomeAskConfirmProps {
  isRunning: boolean;
  onboardingState: TenantOnboarding;
  onPrev: (state: TenantOnboarding) => void;
  onNext: (state: TenantOnboarding) => void;
  standards: ISONorm[];
  selectedLang: Language | undefined;
}

const TrialWelcomeAskConfirm: React.FunctionComponent<ITrialWelcomeAskConfirmProps> = (
  props: ITrialWelcomeAskConfirmProps,
) => {
  const { t } = useTranslation(['translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(props.isRunning);
  const [project, setProject] = useState<CopilotOnboardingProject | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>(undefined);
  const isRunningTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  //Set before the first await; the disabled button follows state and lags a fast second click
  const requestInFlight = useRef<boolean>(false);
  const isMounted = useRef<boolean>(true);
  //Server completion at the start of this run; only a newer server value counts as its completion
  const baselineFinished = useRef<Date | undefined>(props.onboardingState.onboardingSteps.finished);
  const [confirmDeactivateStandards, setConfirmDeactivateStandards] = useState<string | undefined>(undefined);
  const [hasNoPermissionsDeactivateStandards, setHasNoPermissionsDeactivateStandards] = useState<string | undefined>(
    undefined,
  );
  const [phone, setPhone] = useState<string | undefined>(props.onboardingState.onboardingSteps.phone);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean | undefined>(
    isValidNumber(props.onboardingState.onboardingSteps.phone),
  );

  const introImageProps = {
    src: `${Config.getImageURL()}/green-check.png`,
    imageFit: IPImageFit.centerContain,
    width: 24,
    height: 24,
  };

  const clearIsRunningTimer = () => {
    if (isRunningTimer.current) {
      clearTimeout(isRunningTimer.current);
      isRunningTimer.current = undefined;
    }
  };

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      clearIsRunningTimer();
    };
  }, []);

  const getAccessToken = (): Promise<string> => appContext.getAccessToken(apiRequest.scopes);

  const isServerRunning = async (): Promise<boolean> => {
    const result = await apiCopilotFinishOnboardingProgress(await getAccessToken());

    return (result || 0) > 0;
  };

  const isServerRunningSafely = async (): Promise<boolean> => {
    try {
      return await isServerRunning();
    } catch {
      return false;
    }
  };

  const isFinishAlreadyRunning = (err: AppError): boolean =>
    err.code === '409' &&
    AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_Onboarding_AlreadyRunning;

  const finishWithError = (err: AppError) => {
    clearIsRunningTimer();
    requestInFlight.current = false;
    setIsLoading(false);
    setProject(undefined);
    setError(err);
  };

  const scheduleProgressCheck = () => {
    clearIsRunningTimer();
    setIsLoading(true);
    isRunningTimer.current = setTimeout(() => {
      checkProgress();
    }, 2000);
  };

  const checkProgress = async () => {
    //Follows a run another request owns: a reload, a second tab, or a POST whose response was lost
    try {
      if (await isServerRunning()) {
        if (!isMounted.current) return;
        scheduleProgressCheck();
      } else {
        await reconcile();
      }
    } catch (err) {
      if (!isMounted.current) return;
      finishWithError(AppError.fromApiError(err));
    }
  };

  const reconcile = async (errorWhenIncomplete?: AppError) => {
    //The server decides completion: a local timestamp would also mark a failed run as finished.
    const onboardingState = await apiGetTenantOnboardingState(await getAccessToken());
    if (!isMounted.current) return;

    const finished = onboardingState.onboardingSteps.finished;
    const baseline = baselineFinished.current;
    const completed =
      finished !== undefined && (baseline === undefined || finished.getTime() > baseline.getTime());

    if (completed) {
      await refreshContext();
      if (!isMounted.current) return;
      clearIsRunningTimer();
      requestInFlight.current = false;
      appContext.setOnboardingInProgress(false);
      props.onNext(withLocalPhone(onboardingState));
      setIsLoading(false);
    } else {
      finishWithError(errorWhenIncomplete ?? new AppError(t('dashboard:TrialWelcome.Goals.AskConfirmFailure')));
    }
  };

  useEffect(() => {
    //When the isRunning prop is set, the back-end is processing the configuration
    //We need to set this view into loading state and check the progress
    if (props.isRunning) {
      baselineFinished.current = props.onboardingState.onboardingSteps.finished;
      scheduleProgressCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isRunning]);

  const withLocalPhone = (state: TenantOnboarding): TenantOnboarding => {
    const newOnboardingState = state.clone();
    newOnboardingState.onboardingSteps.phone = phone ?? props.onboardingState.onboardingSteps.phone;

    return newOnboardingState;
  };

  //Going back clears the completion fields; the next run sets them again
  const withoutCompletion = (): TenantOnboarding => {
    const newOnboardingState = withLocalPhone(props.onboardingState);
    newOnboardingState.onboardingSteps.finished = undefined;
    newOnboardingState.onboardingSteps.dashboardId = undefined;
    newOnboardingState.onboardingSteps.previewPackageInstalled = undefined;
    newOnboardingState.onboardingSteps.projectMilestonesCreated = undefined;

    return newOnboardingState;
  };

  const getPhoneError = (): string | undefined => {
    if (isPhoneValid === undefined) return undefined;

    return isPhoneValid ? undefined : t('dashboard:TrialWelcome.Phone.Error');
  };

  const checkStartConfig = () => {
    //After the trial the server runs the create steps only: nothing is deactivated or removed, so there is nothing to confirm
    if (!appContext.user.login.isInTrial()) {
      startConfig();

      return;
    }

    //When the new configuration means that we are de-activating standards, we need to check delete permissions and ask for confirmation
    const selectedStandards = getStandards();
    const currentStandards = appContext.globalDataCache.norms.getItemsISO().filter((n) => n.active);
    const deactivatedStandards = currentStandards.filter(
      (n) => !selectedStandards.some((s) => s.isoNormId === n.isoNormId),
    );
    if (deactivatedStandards.length > 0) {
      let userHasPermission = true;
      for (let idx = 0; idx < deactivatedStandards.length; idx++) {
        if (
          !hasUserDataPermission(appContext, [deactivatedStandards[idx].authSchemaId], AuthSchemaLineOperation.Delete)
        ) {
          userHasPermission = false;
          break;
        }
      }
      const deactivatedStandardNames = deactivatedStandards.map((n) => n.name).join(', ');
      if (userHasPermission) {
        setConfirmDeactivateStandards(deactivatedStandardNames);
      } else {
        setHasNoPermissionsDeactivateStandards(deactivatedStandardNames);
      }
    } else {
      startConfig();
    }
  };

  const startConfig = async () => {
    if (requestInFlight.current) return;

    requestInFlight.current = true;
    baselineFinished.current = props.onboardingState.onboardingSteps.finished;
    setIsLoading(true);
    setError(undefined);
    setProject(undefined);

    try {
      //the number typed on this step lives in local state until the finish succeeds; the request must carry it,
      //or HubSpot gets the loaded number (or nothing) instead of the one the user entered
      const newSteps = withLocalPhone(props.onboardingState).onboardingSteps;

      //set translation values for the back-end
      newSteps.dashboardName = t('dashboard:TrialWelcome.Goals.DashboardName');
      newSteps.dashboardWidgetTaskStatusName = t('dashboard:TrialWelcome.Goals.DashboardWidgetTaskStatusName');
      newSteps.dashboardWidgetTaskTagName = t('dashboard:TrialWelcome.Goals.DashboardWidgetTaskTagName');

      const project = await apiCopilotFinishOnboarding(newSteps, await getAccessToken(), appContext.globalDataCache);
      if (!isMounted.current) return;

      setProject(project);
      await reconcile();
    } catch (err) {
      if (!isMounted.current) return;

      //Another request owns the run (a second tab, or this one after the gateway cut its response
      //while the server continues): follow that run instead of reporting a failure
      const appError = AppError.fromApiError(err);
      if (isFinishAlreadyRunning(appError) || (await isServerRunningSafely())) {
        scheduleProgressCheck();
      } else {
        //Not running also means the run may have finished before the probe: only a newer
        //finished value separates a completed run with a lost response from one that never started
        try {
          await reconcile(appError);
        } catch {
          if (!isMounted.current) return;
          finishWithError(appError);
        }
      }
    }
  };

  const refreshContext = async () => {
    await appContext.globalDataCache.refresh();
    appContext.globalDataCache.settings.set(AIEnabled, props.onboardingState.onboardingSteps.optInAI);
  };

  const installPreviewInstant27001 = (): boolean => {
    //preview packages are trial content; after the trial the server does not install them
    if (!appContext.user.login.isInTrial()) return false;

    if (appContext.user.login.leadSource && appContext.user.login.leadSource !== LeadSources_Instant27001) {
      //Do not install the package when the user did not come from Instant27001
      return false;
    }

    const steps = props.onboardingState.onboardingSteps;

    //Install when ISO 27001:2022 is selected
    if (steps.certifiedStandards?.includes(13)) {
      return true;
    }
    if (steps.newStandards?.includes(13)) {
      //do not install the package if it is an upgrade from ISO 27001:2013
      if (!steps.certifiedStandards?.includes(2)) {
        return true;
      }
    }
    if (steps.certifiedOtherStandards?.replaceAll(' ', '')?.toUpperCase().includes('ISO27001')) {
      return true;
    }
    if (steps.newOtherStandards?.replaceAll(' ', '')?.toUpperCase().includes('ISO27001')) {
      //do not install the package if it is an upgrade from ISO 27001:2013
      if (!steps.certifiedStandards?.includes(2)) {
        return true;
      }
    }

    return false;
  };

  const installPreviewInstantNIS2 = (): boolean => {
    //preview packages are trial content; after the trial the server does not install them
    if (!appContext.user.login.isInTrial()) return false;

    const steps = props.onboardingState.onboardingSteps;

    //temp until EN / DE language is available
    const defOrgLang = appContext.globalDataCache.settings.get(DefLanguageCode) as string | undefined;
    if (!defOrgLang || defOrgLang.toLowerCase() !== 'nl') {
      return false;
    }

    //Install when NIS2 level 10,20 or 30 is selected
    if (
      steps.certifiedStandards?.includes(Standards.NIS2_QM10) ||
      steps.certifiedStandards?.includes(Standards.NIS2_QM20) ||
      steps.certifiedStandards?.includes(Standards.NIS2_QM30)
    ) {
      return true;
    }
    if (
      steps.newStandards?.includes(Standards.NIS2_QM10) ||
      steps.newStandards?.includes(Standards.NIS2_QM20) ||
      steps.newStandards?.includes(Standards.NIS2_QM30)
    ) {
      return true;
    }
    if (steps.certifiedOtherStandards?.replaceAll(' ', '')?.toUpperCase().includes('NIS2')) {
      return true;
    }
    if (steps.newOtherStandards?.replaceAll(' ', '')?.toUpperCase().includes('NIS2')) {
      //do not install the package if it is an upgrade from ISO 27001:2013
      if (!steps.certifiedStandards?.includes(2)) {
        return true;
      }
    }

    return false;
  };

  const getStandards = (): ISONorm[] => {
    if (
      props.onboardingState.onboardingSteps.certifiedStandards?.length === 0 &&
      props.onboardingState.onboardingSteps.newStandards?.length === 0
    ) {
      return [];
    }

    const standards: ISONorm[] = [];
    const standardIds = new Set<number>();

    const addStandard = (standard: ISONorm) => {
      if (standardIds.has(standard.isoNormId)) return;

      standardIds.add(standard.isoNormId);
      standards.push(standard);
    };

    props.onboardingState.onboardingSteps.certifiedStandards?.forEach((standardId) => {
      const standard = props.standards.find((s) => s.isoNormId === standardId);
      if (standard) {
        addStandard(standard);
      }
    });

    props.onboardingState.onboardingSteps.newStandards?.forEach((standardId) => {
      const standard = props.standards.find((s) => s.isoNormId === standardId);
      if (standard) {
        //filter out standards that are upgrades of certified standards
        const upgrade = props.standards.find((s) => s.upgradeISONormId === standard.isoNormId);
        if (
          !upgrade ||
          !props.onboardingState.onboardingSteps.certifiedStandards?.some((id) => id === upgrade.isoNormId) ||
          upgrade.allowDowngrade === true
        ) {
          addStandard(standard);
        }
      }
    });

    return standards;
  };

  const getStandardSummaryText = (): string => {
    const selectedStandards = getStandards();
    if (selectedStandards.length === 0) {
      return t('dashboard:TrialWelcome.Goals.Summary.NoStandards');
    }

    const standards: string[] = [];

    selectedStandards.forEach((selectedStandard) => {
      standards.push(selectedStandard.name);
    });

    if (selectedStandards.length === 1) {
      return t('dashboard:TrialWelcome.Goals.Summary.Item1Single', { standards: standards.join(', ') });
    }

    return t('dashboard:TrialWelcome.Goals.Summary.Item1Plural', { standards: standards.join(', ') });
  };

  //
  // Main render
  //
  return (
    <IPStack
      verticalFill
      horizontalAlign="center"
      styles={{ root: { width: 900, paddingRight: 20, paddingBottom: 20 } }}
    >
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        {!project && !error && (
          <IPStack horizontal tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPImage {...introImageProps} />
            </IPStack.Item>
            <IPStack.Item>
              <IPText variant="large">{t('dashboard:TrialWelcome.Goals.AskConfirmInfo')}</IPText>
            </IPStack.Item>
          </IPStack>
        )}
        <IPStack.Item>
          {error && (
            <IPStack tokens={globalStackTokensGapSmall}>
              <IPStack horizontal tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPFontIcon iconName="StatusErrorFull" style={{ fontSize: '24px', color: IPSharedColors.red20 }} />
                </IPStack.Item>
                <IPStack.Item>
                  <IPText variant="mediumPlus">{t('dashboard:TrialWelcome.Goals.AskConfirmFailure')}</IPText>
                </IPStack.Item>
              </IPStack>
              <IPStack.Item>
                <IPText>{error.message}</IPText>
              </IPStack.Item>
            </IPStack>
          )}
          <div style={{ height: 10, overflow: 'hidden' }}>
            {isLoading && <IPProgressIndicator styles={{ root: { height: 10, margin: 0 } }} />}
            {!isLoading && <IPSeparator styles={{ root: { height: 10, margin: 0, padding: 0 } }} />}
          </div>
        </IPStack.Item>
        {!project && (
          <IPStack.Item>
            <IPText variant="large" styles={globalTextStylesBold}>
              {t('dashboard:TrialWelcome.Goals.Summary.Title')}
            </IPText>
            <ul>
              <li>{getStandardSummaryText()}</li>
              <li>{t('dashboard:TrialWelcome.Goals.Summary.Item2')}</li>
              <li>{t('dashboard:TrialWelcome.Goals.Summary.Item3')}</li>
              {installPreviewInstant27001() && <li>{t('dashboard:TrialWelcome.Goals.Summary.Item4')}</li>}
              {installPreviewInstantNIS2() && <li>{t('dashboard:TrialWelcome.Goals.Summary.Item4b')}</li>}
              {!appContext.user.login.isInTrial() && (
                <li>{t('dashboard:TrialWelcome.Goals.Summary.ItemKeepActive')}</li>
              )}
            </ul>
          </IPStack.Item>
        )}
        {!project && (
          <IPStack tokens={globalStackTokensGapExtraSmall}>
            <IPText block variant="large" styles={globalTextStylesBold}>
              {t('dashboard:TrialWelcome.Phone.Title')}
            </IPText>
            <IPText block>{t('dashboard:TrialWelcome.Phone.SubTitle')}</IPText>
          </IPStack>
        )}
        {!project && (
          <IPStack.Item>
            <InternationalPhoneField
              value={phone}
              country={phone ? undefined : translateLanguageCodeToCountryCode(props.selectedLang?.code)}
              onChange={(phone, meta, isValid) => {
                setPhone(phone);
                setIsPhoneValid(isValid);
              }}
              disabled={isLoading}
              autoFocus={true}
              required={true}
              errorMessage={getPhoneError()}
            />
          </IPStack.Item>
        )}
        {!project && (
          <IPStack.Item>
            <IPText block>{t('dashboard:TrialWelcome.Goals.AskConfirmProgress')}</IPText>
            {/* <IPText>{t('dashboard:TrialWelcome.Goals.AskConfirmProgress2')}</IPText> */}
          </IPStack.Item>
        )}
        <IPStack horizontal tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 30 } }}>
          {!project && (
            <IPStack.Item>
              <IPDefaultButton
                disabled={isLoading}
                text={t('dashboard:TrialWelcome.Goals.ButtonPrev')}
                onClick={() => props.onPrev(withoutCompletion())}
              />
            </IPStack.Item>
          )}
          <IPStack.Item>
            <IPPrimaryButton
              disabled={isLoading || !isPhoneValid}
              text={t('translation:General.Button.Continue')}
              onClick={() => {
                checkStartConfig();
              }}
            >
              {isLoading && <IPSpinner size={IPSpinnerSize.small} styles={{ root: { paddingLeft: 5 } }} />}
            </IPPrimaryButton>
          </IPStack.Item>
        </IPStack>
      </IPStack>
      {confirmDeactivateStandards !== undefined && (
        <DialogConfirmDelete
          hidden={confirmDeactivateStandards === undefined}
          title={t('dashboard:TrialWelcome.Goals.AskConfirmDeactivateStandardsTitle')}
          subText={t('dashboard:TrialWelcome.Goals.AskConfirmDeactivateStandardsInfo', {
            names: confirmDeactivateStandards,
          })}
          onYes={() => {
            setConfirmDeactivateStandards(undefined);
            startConfig();
          }}
          onNo={() => setConfirmDeactivateStandards(undefined)}
        />
      )}
      {hasNoPermissionsDeactivateStandards !== undefined && (
        <DialogOk
          hidden={hasNoPermissionsDeactivateStandards === undefined}
          title={t('dashboard:TrialWelcome.Goals.AskConfirmDeactivateStandardsTitle')}
          subText={t('dashboard:TrialWelcome.Goals.AskConfirmDeactivateNoPermissionsInfo', {
            names: hasNoPermissionsDeactivateStandards,
          })}
          onOk={() => {
            setHasNoPermissionsDeactivateStandards(undefined);
          }}
        />
      )}
    </IPStack>
  );
};

export default TrialWelcomeAskConfirm;
